import { Instagram, Linkedin, Mail, ArrowUpRight, Globe, Heart } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import logoWhite from "@/assets/logo-white-long.png";

const navKeys = [
  { key: "footer.nav.inicio", href: "#" },
  { key: "nav.nosotros", href: "#nosotros" },
  { key: "nav.servicios", href: "#servicios" },
  { key: "nav.metodologia", href: "#metodologia" },
  { key: "nav.ia", href: "#ia" },
  { key: "nav.hub", href: "#hub" },
  { key: "nav.equipo", href: "#equipo" },
  { key: "nav.contacto", href: "#contacto" },
];

const socials = [
  { icon: Instagram, href: "https://www.instagram.com/yamper_grow?igsh=MWRuMGI4cmZ5bHNy&utm_source=qr", label: "Instagram" },
  { icon: Linkedin, href: "https://linkedin.com/company/yamper-grow", label: "LinkedIn" },
  { icon: Mail, href: "mailto:yamila.s.vilaverde@gmail.com", label: "Email" },
];

const Footer = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  const scrollTo = (href: string) => {
    if (href === "#") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = document.querySelector(href);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 60;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-foreground text-white pt-20 pb-10 px-6 border-t border-white/10" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="md:col-span-5">
            <motion.p
              className="text-white/40 text-sm leading-relaxed max-w-xs mb-6"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.3 }}
            >
              {t("footer.desc")}
            </motion.p>
            <div className="flex items-center gap-3">
              {socials.map((s, i) => (
                <motion.a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl border border-white/15 flex items-center justify-center text-white/40 hover:text-brand-rosa hover:border-brand-rosa transition-all"
                  aria-label={s.label}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.4 + i * 0.08 }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <s.icon size={18} />
                </motion.a>
              ))}
            </div>
            <img src={logoWhite} alt="Yamper Grow" width={120} height={40} className="h-10 mt-8 opacity-70" loading="lazy" decoding="async" />
          </div>

          <motion.div
            className="md:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/30 mb-5">{t("footer.nav")}</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              {navKeys.map((link, i) => (
                <motion.div
                  key={link.key}
                  initial={{ opacity: 0, x: -10 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 + i * 0.06 }}
                >
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="text-sm text-white/50 hover:text-brand-rosa transition-colors flex items-center gap-1 group"
                  >
                    {t(link.key)}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="md:col-span-4"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/30 mb-5">{t("footer.newsletter")}</p>
            <p className="text-sm text-white/40 mb-5 leading-relaxed">{t("footer.newsletter.desc")}</p>
            <div className="flex gap-0">
              <input
                type="email"
                placeholder="tu@email.com"
                className="flex-1 h-11 bg-white/5 border border-white/10 rounded-l-xl px-4 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-primary transition-colors"
                maxLength={255}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const input = e.currentTarget;
                    if (input.value.trim()) {
                      const subject = encodeURIComponent(t("footer.newsletter.subject").replace("{email}", input.value.trim()));
                      const body = encodeURIComponent(t("footer.newsletter.body").replace("{email}", input.value.trim()));
                      window.open(`mailto:yamila.s.vilaverde@gmail.com?subject=${subject}&body=${body}`, "_self");
                    }
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.querySelector<HTMLInputElement>('footer input[type="email"]');
                  if (input?.value.trim()) {
                    const subject = encodeURIComponent(t("footer.newsletter.subject"));
                    const body = encodeURIComponent(t("footer.newsletter.body").replace("{email}", input.value.trim()));
                    window.open(`mailto:yamila.s.vilaverde@gmail.com?subject=${subject}&body=${body}`, "_self");
                  }
                }}
                className="h-11 px-5 rounded-r-xl bg-white text-foreground text-xs uppercase tracking-[0.1em] font-bold hover:bg-primary hover:text-white transition-colors"
              >
                {t("footer.newsletter.send")}
              </button>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <p className="text-xs text-white/30">{t("footer.copyright")}</p>
          <div className="flex items-center gap-1.5 text-xs text-white/20">
            <Globe size={14} className="text-brand-rosa" />
            <Heart size={12} className="text-brand-rosa fill-brand-rosa" />
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
